import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { guardarSessao, lerToken, lerUsuario, limparSessao, atualizarUsuario } from '../../services/sessao';

export const login = createAsyncThunk('auth/login', async ({ email, password, manterConectado = true }, { rejectWithValue }) => {
  try {
    // O backend usa isto para decidir a validade do token; o front, onde guardá-lo.
    // As duas pontas precisam concordar, senão a sessão morre antes ou depois do
    // que a pessoa escolheu na tela.
    const res = await api.post('/auth/login', { email, password, manterConectado });
    guardarSessao({ token: res.data.token, user: res.data.user, manterConectado });
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Erro ao fazer login');
  }
});

// phoneCodeId/phoneCode: a confirmação do WhatsApp (components/CodigoWhatsapp.jsx).
// Rejeita com { message, code }: o code (PHONE_CODE_*) diz à tela o que fazer com o envio.
export const register = createAsyncThunk('auth/register', async ({ name, email, password, company, phone, cargo, cpfCnpj, phoneCodeId, phoneCode }, { rejectWithValue }) => {
  try {
    // cpfCnpj vai como o usuario digitou (com ou sem mascara): quem valida e normaliza
    // e o backend (utils/documento.js), fonte unica da regra de CPF/CNPJ.
    const res = await api.post('/auth/register', { name, email, password, company, phone, cargo, cpfCnpj, phoneCodeId, phoneCode });
    // Quem acabou de criar a conta fica conectado: mandar a pessoa para o login logo
    // depois de se cadastrar é atrito sem motivo.
    guardarSessao({ token: res.data.token, user: res.data.user, manterConectado: true });
    return res.data;
  } catch (err) {
    return rejectWithValue({
      message: err.response?.data?.error || 'Erro ao criar conta',
      code: err.response?.data?.code || null,
    });
  }
});

export const fetchMe = createAsyncThunk('auth/me', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/auth/me');
    return res.data;
  } catch (err) {
    // Devolve o status junto: so o 401 significa sessao invalida.
    // Erro de rede (sem response) e 429 nao podem deslogar quem estava logado.
    return rejectWithValue({
      status: err.response?.status || null,
      message: err.response?.data?.error || 'Sessão expirada',
    });
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    // A sessão pode estar no localStorage (continuar conectado) ou no sessionStorage
    // (só esta aba). Quem sabe disso é services/sessao.js, não o slice.
    user: lerUsuario(),
    token: lerToken(),
    loading: false,
    error: null,
    errorCode: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      limparSessao();
    },
    clearError: (state) => { state.error = null; state.errorCode = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(register.pending, (state) => { state.loading = true; state.error = null; state.errorCode = null; })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload;
        state.errorCode = action.payload?.code || null;
      })
      .addCase(fetchMe.fulfilled, (state, action) => {
        state.user = action.payload;
        atualizarUsuario(action.payload);
      })
      .addCase(fetchMe.rejected, (state, action) => {
        // Desloga somente quando o token foi de fato recusado (401).
        // 429 (rate limit), 5xx e falha de rede mantem a sessao local.
        if (action.payload?.status === 401) {
          state.user = null;
          state.token = null;
          limparSessao();
        }
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
